import { useState, useEffect, useRef } from 'react';
import gibletLogoGreen from '@/assets/giblet-logo-green.png';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { callGibletApi, ThreadPurpose, GibletBackend } from '@/services/gibletService';
import { KBConnectionPanel } from './KBConnectionPanel';
import { KBChip } from './KBChip';
import { useConnectedKBs } from '@/hooks/useConnectedKBs';
import { useKnowledgeBases, useAccessibleKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { toast } from 'sonner';
import { stripMarkdownFormatting } from '@/lib/stripMarkdown';
import { GibletQuestionForm } from './GibletQuestionForm';
import { SelectionButtons } from './SelectionButtons';
import { GibletChatHistory } from './GibletChatHistory';
import { GibletChronologicalHistory } from './GibletChronologicalHistory';
import { supabase } from '@/integrations/supabase/client';
import { CitationList } from './CitationList';
import { MessageFeedback } from './MessageFeedback';
import { InviteColleagueDialog } from '@/components/InviteColleagueDialog';
import { CreateChannelDialog } from '@/components/channels/CreateChannelDialog';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import { DirectMessageDialog } from '@/components/DirectMessageDialog';
import { getPageName } from '@/utils/pageRouteMapper';

import { CallCreationInlineForm } from './CallCreationInlineForm';
import { ImportContentInlineForm } from './ImportContentInlineForm';
import { SprintCreationInlineForm } from './SprintCreationInlineForm';
import { KBCreationInlineForm } from './KBCreationInlineForm';
import { useCallCreationFlow, CallType } from '@/hooks/useCallCreationFlow';
import { KBSearchResults, parseKBResultsFromResponse, KBSearchResult } from './KBSearchResults';
import { GibletInputArea } from './GibletInputArea';
import { GibletThinkingIndicator } from './GibletThinkingIndicator';
import { EngageAction } from './GibletEngageMenu';
import {
  CallTypeMessage,
  FunctionalAreaMessage,
  BountyMessage,
  BountyDetailsMessage,
  CallDetailsMessage,
  RolesMessage,
  ReviewMessage,
  ChannelSourceSelectionMessage,
  CfKConnectionMessage,
  CfKBountyConnectionMessage,
} from './CallCreationChatMessages';
import { useUserChannels } from '@/hooks/useUserChannels';

interface Citation {
  kbTitle: string;
  kbDescription?: string;
  snippet: string;
  relevance: number;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
}

const getGibletVariant = (pathname: string): string => {
  return 'Giblet Chat';
};

export const GibletChatPanel = ({ initialCallType, initialMessage, initialEngageAction, onClose }: { initialCallType?: 'solution' | 'knowledge' | 'destruction' | 'start-call-flow' | 'create-kb' | 'team-sprint' | null; initialMessage?: string | null; initialEngageAction?: EngageAction | null; onClose?: () => void }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>('Thinking...');
  const [showThreadHistory, setShowThreadHistory] = useState(false);
  const [showChronological, setShowChronological] = useState(false);
  const [showKBPanel, setShowKBPanel] = useState(false);
  const [submittedFormMessageIdx, setSubmittedFormMessageIdx] = useState<number | null>(null);
  const [workflowState, setWorkflowState] = useState<Record<string, any>>({});
  const [selectedMessageIdx, setSelectedMessageIdx] = useState<number | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | undefined>();
  const zoomLevel = 100; // Fixed at +2 from smallest (100%)
  const [showCallForm, setShowCallForm] = useState(false);
  const [preselectedCallType, setPreselectedCallType] = useState<'solution' | 'knowledge' | 'destruction' | null>(null);
  const [isDraftConversation, setIsDraftConversation] = useState(true);
  const [isCreatingCall, setIsCreatingCall] = useState(false);
  const [threadPurpose, setThreadPurpose] = useState<ThreadPurpose>('general');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [dmDialogOpen, setDmDialogOpen] = useState(false);
  const [isCreatingSprint, setIsCreatingSprint] = useState(false);
  const [isImportingContent, setIsImportingContent] = useState(false);
  const [isKBCreationFlow, setIsKBCreationFlow] = useState(false);
  const [kbFlowData, setKbFlowData] = useState<{
    sourceType: 'channel' | 'personal' | null;
    sourceChannelId: string | null;
    channelName: string;
    domainId: string | null;
    domainName: string;
  }>({
    sourceType: null,
    sourceChannelId: null,
    channelName: '',
    domainId: null,
    domainName: '',
  });
  // Always use Lovable AI backend (model selection removed from UI)
  const gibletBackend: GibletBackend = 'lovable-ai';
  
  const callFlow = useCallCreationFlow(preselectedCallType);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef<HTMLDivElement>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { data: userChannels } = useUserChannels(userId);
  const { data: allKBs } = useKnowledgeBases();
  
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id);
      if (user?.id) {
        loadOrCreateConversation(user.id);
      }
    });
  }, []);
  
  const { data: accessibleKBs } = useAccessibleKnowledgeBases(userId);
  const maxConnectedKBs = accessibleKBs?.length || 50;
  
  const {
    connectedKBs,
    connectKB,
    disconnectKB,
    setConnectedKBsDirectly,
    canConnectMore,
    getRecentlyUsed
  } = useConnectedKBs(conversationId, maxConnectedKBs);

  const variant = getGibletVariant(location.pathname);
  const variantKey = 'chat';

  useEffect(() => {
    if (messages.length <= 1) {
      // New workflow started — scroll to top so user sees the first message
      scrollTopRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Auto-send initial message if provided
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (initialMessage && !initialMessageSentRef.current && userId) {
      initialMessageSentRef.current = true;
      // Use setTimeout to ensure handleSend is ready
      setTimeout(() => handleSend(initialMessage), 100);
    }
  }, [initialMessage, userId]);

  // Auto-fire initial engage action if provided
  const initialEngageActionFiredRef = useRef(false);
  useEffect(() => {
    if (initialEngageAction && !initialEngageActionFiredRef.current) {
      initialEngageActionFiredRef.current = true;
      handleEngageAction(initialEngageAction);
    }
  }, [initialEngageAction]);

  // Handle initialCallType from dropdown or megaphone button
  // UNIFIED FLOW: Always start with channel selection first
  useEffect(() => {
    if (initialCallType && !isCreatingCall && !isKBCreationFlow && !isCreatingSprint) {
      if (initialCallType === 'create-kb') {
        // Start KB creation flow with channel selection
        setThreadPurpose('kb_work');
        setIsKBCreationFlow(true);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'kb-flow-channel-source',
        }]);
      } else if (initialCallType === 'team-sprint') {
        // Start sprint creation flow
        setThreadPurpose('team_collaboration');
        setIsCreatingSprint(true);
      } else {
        // Set thread purpose for call creation
        setThreadPurpose('call_creation');
        setIsCreatingCall(true);
        
        if (initialCallType === 'start-call-flow') {
          // Generic start - begin with channel selection, then type selection
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: 'call-flow-channel-source',
          }]);
        } else {
          // Pre-selected call type (CfS, CfK, CfD) - still start with channel but set the type
          setPreselectedCallType(initialCallType);
          callFlow.updateFormData('type', initialCallType);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: 'call-flow-channel-source',
          }]);
        }
      }
    }
  }, [initialCallType, isCreatingCall, isKBCreationFlow, isCreatingSprint]);

  // Detect thread purpose based on context
  const detectThreadPurpose = (): ThreadPurpose => {
    // If actively creating a call, use call_creation
    if (isCreatingCall || initialCallType) return 'call_creation';
    
    // If KBs are connected, likely kb_work
    if (connectedKBs.length > 0) return 'kb_work';
    
    // Check page context for team-related pages
    const pathname = location.pathname;
    if (pathname.includes('/channel') || pathname.includes('/team') || pathname.includes('/network')) {
      return 'team_collaboration';
    }
    
    // Default to general
    return 'general';
  };

  const loadOrCreateConversation = async (uid: string) => {
    try {
      // If starting a call flow, skip loading existing conversation
      // and start fresh with a new draft thread
      if (initialCallType) {
        setConversationId(null);
        setIsDraftConversation(true);
        // Don't reset messages - let the call flow useEffect handle it
        return;
      }

      // Try to get active conversation for this variant
      const { data: activeConv, error: activeError } = await supabase
        .from('giblet_conversations')
        .select('*')
        .eq('user_id', uid)
        .eq('giblet_variant', variantKey)
        .eq('is_active', true)
        .maybeSingle();

      if (activeError) throw activeError;

      if (activeConv) {
        // Load existing conversation
        setConversationId(activeConv.id);
        setIsDraftConversation(false);
        
        // Restore connected KBs from metadata if present
        if (activeConv.metadata) {
          const metadata = activeConv.metadata as Record<string, any>;
          if (Array.isArray(metadata.connected_kb_ids)) {
            setConnectedKBsDirectly(metadata.connected_kb_ids);
          }
        }
        
        await loadConversationMessages(activeConv.id);
      } else {
        // Set up draft state - NO DATABASE INSERT
        setConversationId(null);
        setMessages([]);
        setIsDraftConversation(true);
      }
    } catch (error: any) {
      console.error('Error loading conversation:', error);
    }
  };

  const createNewConversation = async (uid: string) => {
    try {
      const { data, error } = await supabase
        .from('giblet_conversations')
        .insert({
          user_id: uid,
          title: 'New Conversation',
          giblet_variant: variantKey,
          page_context: location.pathname,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      setConversationId(data.id);
      setMessages([]);
    } catch (error: any) {
      console.error('Error creating conversation:', error);
    }
  };

  const loadConversationMessages = async (convId: string) => {
    try {
      const { data, error } = await supabase
        .from('giblet_chat_messages')
        .select('*')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const loadedMessages: Message[] = (data || []).map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
      }));

      setMessages(loadedMessages);
    } catch (error: any) {
      console.error('Error loading messages:', error);
    }
  };

  // Generate AI-powered thread title from first message exchange
  const generateThreadTitle = async (userMessage: string, assistantResponse: string, convId: string) => {
    try {
      const titlePrompt = `Generate a concise 3-6 word title summarizing this conversation. Reply with ONLY the title, no quotes, colons, or extra punctuation.

User: ${userMessage.slice(0, 200)}
Assistant: ${assistantResponse.slice(0, 200)}`;

      const result = await callGibletApi(titlePrompt, '', [], [], {}, undefined, 'general', gibletBackend);
      let title = result.response.trim().replace(/^["']|["']$/g, '').slice(0, 60);
      
      if (!title || title.length < 3) {
        title = userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : '');
      }

      await supabase
        .from('giblet_conversations')
        .update({ title })
        .eq('id', convId);
        
      // Refresh conversation list to show new title
      if (userId) {
        const { data } = await supabase
          .from('giblet_conversations')
          .select('*')
          .eq('user_id', userId)
          .eq('giblet_variant', 'chat')
          .eq('is_active', true);
        // Title will update in the history panel on next render
      }
    } catch (error) {
      console.error('Error generating thread title:', error);
      // Fallback: use truncated first message
      const fallbackTitle = userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : '');
      await supabase
        .from('giblet_conversations')
        .update({ title: fallbackTitle })
        .eq('id', convId);
    }
  };

  const saveMessage = async (message: Message) => {
    if (!conversationId || !userId) return;

    try {
      await supabase
        .from('giblet_chat_messages')
        .insert({
          conversation_id: conversationId,
          user_id: userId,
          role: message.role,
          content: message.content,
          connected_kb_ids: connectedKBs,
          workflow_state: workflowState,
        });

      // Update conversation last_message_at
      const updates: any = { last_message_at: new Date().toISOString() };
      

      await supabase
        .from('giblet_conversations')
        .update(updates)
        .eq('id', conversationId);
    } catch (error: any) {
      console.error('Error saving message:', error);
    }
  };

  const handleSelectConversation = async (convId: string) => {
    if (!userId) return;

    try {
      // If current is a draft (unsaved), just discard it
      if (isDraftConversation) {
        // No database cleanup needed - it was never saved
        setIsDraftConversation(false);
      } else if (conversationId) {
        // Mark previous saved conversation as not active
        await supabase
          .from('giblet_conversations')
          .update({ is_active: false })
          .eq('id', conversationId);
      }

      // Mark selected as active and fetch metadata for connected KBs
      const { data: selectedConv } = await supabase
        .from('giblet_conversations')
        .update({ is_active: true })
        .eq('id', convId)
        .select('metadata')
        .single();

      setConversationId(convId);
      setIsDraftConversation(false);
      
      // Restore connected KBs from metadata if present
      if (selectedConv?.metadata) {
        const metadata = selectedConv.metadata as Record<string, any>;
        if (Array.isArray(metadata.connected_kb_ids)) {
          setConnectedKBsDirectly(metadata.connected_kb_ids);
        }
      }
      
      await loadConversationMessages(convId);
      setShowThreadHistory(false);
    } catch (error: any) {
      console.error('Error selecting conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  const handleNewConversation = async () => {
    if (!userId) return;

    try {
      // If current is draft, just reset state
      if (isDraftConversation) {
        setMessages([]);
        setInput('');
        setShowThreadHistory(false);
        return;
      }
      
      // Mark current conversation as not active
      if (conversationId) {
        await supabase
          .from('giblet_conversations')
          .update({ is_active: false })
          .eq('id', conversationId);
      }

      // Set up new draft state
      setConversationId(null);
      setMessages([]);
      setIsDraftConversation(true);
      setShowThreadHistory(false);
    } catch (error: any) {
      console.error('Error creating new conversation:', error);
      toast.error('Failed to create new conversation');
    }
  };

  const buildUserContext = () => {
    const pageName = location.pathname;
    let context = `Current page: ${pageName}`;
    
    if (profile) {
      context += `\nUser: ${profile.display_name}`;
      if (profile.organization) context += `\nOrganization: ${profile.organization}`;
      if (profile.title) context += `\nTitle: ${profile.title}`;
    }
    
    return context;
  };

  // Call type abbreviations for thread titles
  const CALL_TYPE_ABBR: Record<string, string> = {
    'solution': 'CfS',
    'knowledge': 'CfK',
    'destruction': 'CfD'
  };

  const handleCallFormComplete = async (formData: any) => {
    setShowCallForm(false);
    
    // Generate structured title for the new Call thread
    const callTypeAbbr = CALL_TYPE_ABBR[formData.type.toLowerCase()] || 'Call';
    const functionalArea = formData.functionalArea.substring(0, 3).toUpperCase();
    const topic = formData.title.length > 30 ? formData.title.substring(0, 30) + '...' : formData.title;
    const threadTitle = `${callTypeAbbr}: ${functionalArea} - ${topic}`;
    
    if (!userId) return;
    
    try {
      // Mark current conversation as not active
      if (conversationId) {
        await supabase
          .from('giblet_conversations')
          .update({ is_active: false })
          .eq('id', conversationId);
      }
      
      // Create NEW conversation for this Call
      const { data: newConv, error: convError } = await supabase
        .from('giblet_conversations')
        .insert({
          user_id: userId,
          title: threadTitle,
          giblet_variant: variantKey,
          page_context: location.pathname,
          is_active: true,
          metadata: {
            call_type: formData.type,
            functional_area: formData.functionalArea,
            call_topic: formData.title,
            is_call_thread: true,
            bounty_amount: formData.isBounty ? formData.bountyAmount : null
          }
        })
        .select()
        .single();
      
      if (convError) throw convError;
      
      setConversationId(newConv.id);
      setIsDraftConversation(false);
      setMessages([]);
      
      // Create a formatted summary for Giblet AI to review
      const callSummary = `I'd like to create a Call for ${formData.type}:

**Type:** ${formData.type}
**Functional Area:** ${formData.functionalArea}
**Title:** ${formData.title}
**Bounty:** ${formData.isBounty ? `$${formData.bountyAmount} (Deadline: ${formData.deadline})` : 'None'}

**Description:**
${formData.description}

**Requirements:**
${formData.requirements}

**Roles:**
- Creator: ${formData.creator}
- Confirmer: ${formData.confirmer}
${formData.contributors.length > 0 ? `- Contributors: ${formData.contributors.join(', ')}` : ''}

Please review this call and help me finalize it. Can you suggest any improvements or confirm it's ready to submit?`;

      const userMessage: Message = {
        role: 'user',
        content: callSummary
      };
      
      setMessages([userMessage]);
      await saveMessage(userMessage);
      
      setIsLoading(true);
      
      // Get Giblet's response
      const result = await callGibletApi(
        callSummary,
        buildUserContext(),
        [],
        connectedKBs,
        workflowState,
        undefined,
        'call_creation',
        gibletBackend
      );
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: result.response,
        citations: result.citations
      };
      
      setMessages([userMessage, assistantMessage]);
      await saveMessage(assistantMessage);
      
      toast.success(`New Call thread created: ${threadTitle}`);
    } catch (error) {
      console.error('Error creating Call thread:', error);
      toast.error('Failed to create Call thread');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallFormCancel = () => {
    setShowCallForm(false);
    setPreselectedCallType(null);
  };


  // Detect if user is confirming call creation intent
  const detectCallCreationIntent = (message: string, conversationHistory: Message[]): { shouldTriggerFlow: boolean; callType?: CallType } => {
    const lowerMessage = message.toLowerCase().trim();
    
    // Check recent assistant messages for call creation offers
    const recentAssistantMessages = conversationHistory
      .slice(-4)
      .filter(m => m.role === 'assistant')
      .map(m => m.content.toLowerCase());
    
    const hadCallCreationOffer = recentAssistantMessages.some(content => 
      content.includes('call for solution') || 
      content.includes('call for knowledge') || 
      content.includes('call for destruction') ||
      content.includes('create a call') ||
      content.includes('setting up a call')
    );
    
    if (!hadCallCreationOffer) return { shouldTriggerFlow: false };
    
    // Check if user is confirming
    const confirmationPatterns = [
      /^yes/i, /^yeah/i, /^yep/i, /^sure/i, /^ok/i, /^okay/i, /^let'?s do it/i,
      /^let'?s go/i, /^sounds good/i, /^go ahead/i, /^please/i, /^definitely/i,
      /^absolutely/i, /^create it/i, /^start it/i, /^proceed/i
    ];
    
    const isConfirming = confirmationPatterns.some(pattern => pattern.test(lowerMessage));
    
    if (isConfirming) {
      // Determine call type from context
      const contextText = recentAssistantMessages.join(' ');
      let callType: CallType = 'solution';
      if (contextText.includes('call for knowledge')) callType = 'knowledge';
      else if (contextText.includes('call for destruction')) callType = 'destruction';
      
      return { shouldTriggerFlow: true, callType };
    }
    
    // Also detect direct requests
    if (lowerMessage.includes('create') && lowerMessage.includes('call')) {
      let callType: CallType = 'solution';
      if (lowerMessage.includes('knowledge')) callType = 'knowledge';
      else if (lowerMessage.includes('destruction')) callType = 'destruction';
      return { shouldTriggerFlow: true, callType };
    }
    
    return { shouldTriggerFlow: false };
  };

  const handleSend = async (overrideInput?: string) => {
    const messageText = overrideInput || input;
    if (!messageText.trim() || isLoading) return;

    // Check for call creation intent BEFORE sending to AI
    const callIntent = detectCallCreationIntent(messageText, messages);
    
    if (callIntent.shouldTriggerFlow && !isCreatingCall) {
      // User confirmed call creation - trigger inline form flow instead of text response
      setInput('');
      setThreadPurpose('call_creation');
      setIsCreatingCall(true);
      
      // Add user's confirmation message
      const userMessage: Message = { role: 'user', content: messageText };
      setMessages(prev => [...prev, userMessage]);
      
      // If we detected a specific call type, skip to functional area
      if (callIntent.callType) {
        setPreselectedCallType(callIntent.callType);
        callFlow.updateFormData('type', callIntent.callType);
        setMessages(prev => [...prev, { role: 'assistant', content: 'call-flow-functional-area' }]);
      } else {
        // Start from type selection
        setMessages(prev => [...prev, { role: 'assistant', content: 'call-flow-type' }]);
      }
      return;
    }

    // If this is a draft conversation, create it NOW
    if (isDraftConversation && userId) {
      try {
        const { data, error } = await supabase
          .from('giblet_conversations')
          .insert({
            user_id: userId,
            title: 'New Conversation',
            giblet_variant: variantKey,
            page_context: location.pathname,
            is_active: true,
          })
          .select()
          .single();
          
        if (error) {
          console.error('Error creating conversation:', error);
          toast.error('Failed to create conversation');
          return;
        }
        
        setConversationId(data.id);
        setIsDraftConversation(false);
      } catch (error) {
        console.error('Error creating conversation:', error);
        toast.error('Failed to create conversation');
        return;
      }
    }

    const userMessage: Message = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Auto-save user message
    await saveMessage(userMessage);

    const updatedWorkflow = { ...workflowState };
    setWorkflowState(updatedWorkflow);

    try {
      const currentThreadPurpose = detectThreadPurpose();
      const result = await callGibletApi(
        messageText,
        buildUserContext(),
        messages,
        connectedKBs,
        updatedWorkflow,
        conversationId || undefined,
        currentThreadPurpose,
        gibletBackend
      );

      const assistantMessage: Message = { 
        role: 'assistant', 
        content: result.response,
        citations: result.citations 
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Auto-save assistant message
      await saveMessage(assistantMessage);
      
      // Generate AI title after first exchange (1 user message = messages.length was 0 before adding user msg)
      if (messages.length === 0 && conversationId) {
        generateThreadTitle(messageText, result.response, conversationId);
      }
    } catch (error) {
      toast.error('Failed to get response from Giblet');
      console.error('Giblet error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (answers: Record<string, any>, messageIdx: number) => {
    setSubmittedFormMessageIdx(messageIdx);
    
    const updatedWorkflow = { ...workflowState, ...answers };
    setWorkflowState(updatedWorkflow);
    
    const formattedAnswers = Object.entries(answers)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
    
    const userMessage: Message = { 
      role: 'user', 
      content: `Here are my answers:\n\n${formattedAnswers}` 
    };
    
    setMessages(prev => [...prev, userMessage]);
    await saveMessage(userMessage);
    setIsLoading(true);

    try {
      const currentThreadPurpose = detectThreadPurpose();
      const result = await callGibletApi(
        userMessage.content,
        buildUserContext(),
        messages,
        connectedKBs,
        updatedWorkflow,
        conversationId || undefined,
        currentThreadPurpose,
        gibletBackend
      );

      const assistantMessage: Message = { 
        role: 'assistant', 
        content: result.response,
        citations: result.citations 
      };
      setMessages(prev => [...prev, assistantMessage]);
      await saveMessage(assistantMessage);
    } catch (error) {
      toast.error('Failed to get response from Giblet');
      console.error('Giblet error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const detectSelectableOptions = (content: string): string[] | null => {
    // Detect bulleted options (2+ items with dashes or asterisks)
    const lines = content.split('\n');
    const bulletedLines = lines.filter(line => /^[-*]\s+/.test(line.trim()));
    
    // Show buttons for 2+ bulleted options (lowered threshold for inline selection style)
    if (bulletedLines.length >= 2) {
      return bulletedLines.map(line => line.trim());
    }
    
    // Also check for numbered lists
    const numberedLines = lines.filter(line => /^\d+\.\s+/.test(line.trim()));
    
    if (numberedLines.length >= 2) {
      return numberedLines.map(line => {
        const cleaned = line.trim().replace(/^\d+\.\s+/, '');
        return `- ${cleaned}`;
      });
    }
    
    return null;
  };

  // Store recent KB search results for context-aware navigation
  const [recentKBResults, setRecentKBResults] = useState<Array<{id: string, title: string}>>([]);

  const handleOptionSelect = async (option: string, messageIdx: number) => {
    if (isLoading) return;
    setSelectedMessageIdx(messageIdx);
    const cleanOption = option.split('(')[0].trim();
    const optionLower = cleanOption.toLowerCase();
    
    // KB-context routes that need a KB ID
    const kbContextRoutes: Record<string, (id: string) => string> = {
      'view details': (id) => `/knowledge-base/${id}`,
      'review page': (id) => `/knowledge-base/${id}`,
      'review': (id) => `/knowledge-base/${id}`,
      'kb review': (id) => `/knowledge-base/${id}`,
      'open kb': (id) => `/knowledge-base/${id}`,
      'kb details': (id) => `/knowledge-base/${id}`,
      'details': (id) => `/knowledge-base/${id}`,
      'edit kb': (id) => `/edit-knowledge-base/${id}`,
      'edit': (id) => `/edit-knowledge-base/${id}`,
    };
    
    // Marketplace/access routes
    const marketplaceRoutes: Record<string, (id: string) => string> = {
      'request access': (id) => `/marketplace?kb=${id}`,
      'acquire access': (id) => `/marketplace?kb=${id}`,
      'get access': (id) => `/marketplace?kb=${id}`,
    };
    
    // Static routes that don't need context
    const staticRoutes: Record<string, string> = {
      'creators': '/creators',
      'my profile': '/myspai-profile',
      'profile': '/myspai-profile',
      'dashboard': '/creators',
      'home': '/creators',
      'marketplace': '/marketplace',
      'channels': '/channels',
      'sprints': '/prompt-architecture-sprint',
      'create kb': '/create-knowledge-base',
      'new kb': '/create-knowledge-base',
    };
    
    // Check for navigation intent
    const hasNavigationIntent = (text: string): boolean => {
      const patterns = ['open the', 'go to', 'navigate to', 'view the', 'open', 'visit', 'see the'];
      return patterns.some(p => text.toLowerCase().includes(p));
    };
    
    // Resolve KB ID from context (connected KBs, recent results, option text)
    const resolveKBFromContext = (): { id: string, title: string } | null => {
      // 1. Check if option text matches a KB title in accessible KBs
      if (accessibleKBs && accessibleKBs.length > 0) {
        const matchedKB = accessibleKBs.find(kb => 
          optionLower.includes(kb.title.toLowerCase()) ||
          kb.title.toLowerCase().includes(optionLower)
        );
        if (matchedKB) return { id: matchedKB.id, title: matchedKB.title };
      }
      
      // 2. Check recent KB search results
      if (recentKBResults.length > 0) {
        const matchedResult = recentKBResults.find(kb =>
          optionLower.includes(kb.title.toLowerCase()) ||
          kb.title.toLowerCase().includes(optionLower)
        );
        if (matchedResult) return matchedResult;
      }
      
      // 3. Use first connected KB if available
      if (connectedKBs.length > 0 && accessibleKBs) {
        const connectedKB = accessibleKBs.find(kb => kb.id === connectedKBs[0]);
        if (connectedKB) return { id: connectedKB.id, title: connectedKB.title };
      }
      
      return null;
    };
    
    // Handle navigation with context awareness
    if (hasNavigationIntent(cleanOption)) {
      // First check KB-context routes
      for (const [key, routeBuilder] of Object.entries(kbContextRoutes)) {
        if (optionLower.includes(key)) {
          const kbContext = resolveKBFromContext();
          if (kbContext) {
            const route = routeBuilder(kbContext.id);
            toast.success(`Opening ${kbContext.title}...`);
            navigate(route);
            return;
          } else {
            // No KB context - ask Giblet to help find one
            toast.info('Please specify which Knowledge Base you want to view.');
            handleSend(`Which Knowledge Base would you like me to ${key}?`);
            return;
          }
        }
      }
      
      // Check marketplace routes
      for (const [key, routeBuilder] of Object.entries(marketplaceRoutes)) {
        if (optionLower.includes(key)) {
          const kbContext = resolveKBFromContext();
          if (kbContext) {
            const route = routeBuilder(kbContext.id);
            toast.info(`Redirecting to marketplace for ${kbContext.title}...`);
            navigate(route);
            return;
          } else {
            navigate('/marketplace');
            return;
          }
        }
      }
      
      // Check static routes
      for (const [key, route] of Object.entries(staticRoutes)) {
        if (optionLower.includes(key)) {
          toast.success(`Navigating to ${route.replace(/^\//, '').replace(/-/g, ' ')}`);
          navigate(route);
          return;
        }
      }
    }
    
    // Check if selected option matches an accessible KB and auto-connect it
    if (accessibleKBs && accessibleKBs.length > 0) {
      const matchedKB = accessibleKBs.find(kb => {
        const kbTitle = kb.title.toLowerCase();
        return kbTitle === optionLower || 
               kbTitle.includes(optionLower) || 
               optionLower.includes(kbTitle);
      });
      
      if (matchedKB && !connectedKBs.includes(matchedKB.id)) {
        connectKB(matchedKB.id);
        toast.success(`Connected: ${matchedKB.title}`);
      }
    }
    
    // Get the assistant message that presented these options
    const assistantMessage = messages[messageIdx];
    const optionsPresented = detectSelectableOptions(assistantMessage?.content || '');
    
    // Determine decision category based on context
    const determineCategory = (): string => {
      const content = assistantMessage?.content?.toLowerCase() || '';
      if (content.includes('call type') || content.includes('what type')) return 'call_type';
      if (content.includes('domain') || content.includes('functional area')) return 'domain';
      if (content.includes('bounty')) return 'bounty';
      if (content.includes('ready to proceed') || content.includes('confirm')) return 'confirmation';
      if (content.includes('rate') || content.includes('rating')) return 'rating';
      if (content.includes('help with') || content.includes('assist')) return 'intent';
      return 'general';
    };
    
    // Save decision to database for preference learning
    if (userId && optionsPresented) {
      try {
        await supabase
          .from('user_giblet_decisions')
          .insert({
            user_id: userId,
            conversation_id: conversationId,
            decision_category: determineCategory(),
            options_presented: optionsPresented,
            option_selected: cleanOption,
            context: assistantMessage?.content?.substring(0, 500) || null,
          });
        console.log('Decision tracked:', { category: determineCategory(), selected: cleanOption });
      } catch (error) {
        console.error('Error tracking decision:', error);
      }
    }
    
    setTimeout(() => {
      handleSend(cleanOption);
    }, 0);
  };

  // Handle channel source selection (FIRST step in ALL call flows)
  const handleChannelSourceSelect = (sourceType: 'channel' | 'personal', channelId?: string) => {
    callFlow.updateFormData('sourceType', sourceType);
    callFlow.updateFormData('sourceChannelId', channelId || null);
    
    const channelInfo = userChannels?.find(c => c.channel_id === channelId);
    const sourceName = sourceType === 'personal' 
      ? 'Personal' 
      : channelInfo?.channels.name || 'Channel';
    
    callFlow.updateFormData('channelName', sourceName);
    
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `From: ${sourceName}` },
    ]);
    
    // After channel selection, check if we have a preselected type
    if (preselectedCallType || callFlow.formData.type) {
      // Type already selected, go to functional area
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'call-flow-functional-area' }
      ]);
    } else {
      // No type selected yet, ask for call type
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'call-flow-type' }
      ]);
    }
  };

  // Handle call type selection (comes after channel)
  const handleCallTypeSelect = (type: CallType) => {
    callFlow.updateFormData('type', type);
    setPreselectedCallType(type);
    const typeLabel = type === 'solution' ? 'Solution' : type === 'knowledge' ? 'Knowledge' : 'Destruction';
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Call for ${typeLabel}` },
      { role: 'assistant', content: 'call-flow-functional-area' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleFunctionalAreaSelect = (area: string, areaName?: string) => {
    callFlow.updateFormData('functionalArea', area);
    callFlow.updateFormData('functionalAreaName', areaName || area);
    const areaLabel = areaName || [
      { id: 'technical-architecture', name: 'Technical Architecture' },
      { id: 'web3-integration', name: 'Web3 Integration' },
      { id: 'user-experience', name: 'User Experience (UX/UI)' },
      { id: 'compliance-governance', name: 'Compliance & Governance' },
      { id: 'ai-automation', name: 'AI & Automation' },
      { id: 'knowledge-management', name: 'Knowledge Management' },
      { id: 'branding', name: 'Branding' },
      { id: 'value-proposition', name: 'Value Proposition' }
    ].find(a => a.id === area)?.name || area;
    
    setMessages(prev => [
      ...prev,
      { role: 'user', content: areaLabel },
    ]);
    
    // For CfK, ask about CfS connection first
    if (callFlow.formData.type === 'knowledge') {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'call-flow-cfk-connection' }
      ]);
    } else {
      // For CfS and CfD, go directly to bounty
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'call-flow-bounty' }
      ]);
    }
    callFlow.progressToNextStep();
  };

  // CfK-specific: Handle CfS connection question
  const handleCfKConnectionSelect = (connected: boolean) => {
    callFlow.updateFormData('connectedToCfS', connected);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: connected ? 'Yes, connected to a CfS' : 'No, standalone CfK' },
    ]);
    
    if (connected) {
      // Ask if the CfS has a bounty
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'call-flow-cfk-bounty' }
      ]);
    } else {
      // Go to bounty question
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'call-flow-bounty' }
      ]);
    }
    callFlow.progressToNextStep();
  };

  // CfK-specific: Handle CfS bounty question
  const handleCfKBountySelect = (hasBounty: boolean) => {
    callFlow.updateFormData('cfSHasBounty', hasBounty);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: hasBounty ? 'Yes, CfS has a bounty' : 'No bounty on CfS' },
      { role: 'assistant', content: 'call-flow-bounty' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleBountySelect = (isBounty: boolean) => {
    callFlow.updateFormData('isBounty', isBounty);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: isBounty ? 'Yes, add a bounty' : 'No bounty' },
      { role: 'assistant', content: isBounty ? 'call-flow-bounty-details' : 'call-flow-details' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleBountyDetailsSubmit = (amount: string, deadline: string) => {
    callFlow.updateFormData('bountyAmount', amount);
    callFlow.updateFormData('deadline', deadline);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Bounty: $${amount}, Deadline: ${deadline}` },
      { role: 'assistant', content: 'call-flow-details' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleCallDetailsSubmit = (title: string, description: string, requirements: string) => {
    callFlow.updateFormData('title', title);
    callFlow.updateFormData('description', description);
    callFlow.updateFormData('requirements', requirements);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Title: ${title}` },
      { role: 'assistant', content: 'call-flow-roles' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleRolesSubmit = (creator: string, confirmer: string, contributors: string[]) => {
    callFlow.updateFormData('creator', creator);
    callFlow.updateFormData('confirmer', confirmer);
    callFlow.updateFormData('contributors', contributors);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `Creator: ${creator}` },
      { role: 'assistant', content: 'call-flow-review' }
    ]);
    callFlow.progressToNextStep();
  };

  const handleCallReviewConfirm = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('calls')
        .insert({
          title: callFlow.formData.title,
          description: callFlow.formData.description,
          bounty_amount: callFlow.formData.isBounty ? parseFloat(callFlow.formData.bountyAmount) : null,
          deadline: callFlow.formData.deadline || null,
          creator_id: userId!,
          status: 'open',
        })
        .select()
        .single();

      if (error) throw error;

      // Also insert into giblet_call_history to keep metrics in sync
      await supabase.from('giblet_call_history').insert({
        user_id: userId!,
        call_type: callFlow.formData.type || 'solution',
        call_input: `Created via inline form: ${callFlow.formData.title}`,
        call_output: `Call created successfully`,
        call_data: {
          title: callFlow.formData.title,
          description: callFlow.formData.description,
          bountyAmount: callFlow.formData.bountyAmount,
          deadline: callFlow.formData.deadline,
          functionalArea: callFlow.formData.functionalArea,
        },
        status: 'sent',
      });

      setMessages(prev => [
        ...prev,
        { role: 'user', content: 'Confirm submission' },
        { role: 'assistant', content: `✅ Your Call has been successfully created! You can view and manage it in the Engage section.` }
      ]);
      
      toast.success('Call created successfully!');
      setIsCreatingCall(false);
      callFlow.reset();
    } catch (error) {
      console.error('Error creating call:', error);
      toast.error('Failed to create call. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallReviewEdit = () => {
    setMessages(prev => [
      ...prev,
      { role: 'user', content: 'Edit call details' },
      { role: 'assistant', content: 'Let me help you edit. Which section would you like to modify?' }
    ]);
  };

  // KB Creation Flow Handlers
  const handleKBChannelSourceSelect = (sourceType: 'channel' | 'personal', channelId?: string) => {
    const channelInfo = userChannels?.find(c => c.channel_id === channelId);
    const sourceName = sourceType === 'personal' 
      ? 'Personal' 
      : channelInfo?.channels.name || 'Channel';
    
    setKbFlowData(prev => ({
      ...prev,
      sourceType,
      sourceChannelId: channelId || null,
      channelName: sourceName,
    }));
    
    setMessages(prev => [
      ...prev,
      { role: 'user', content: `From: ${sourceName}` },
      { role: 'assistant', content: 'kb-flow-domain' }
    ]);
  };

  const handleKBDomainSelect = (domainId: string, domainName: string) => {
    setKbFlowData(prev => ({
      ...prev,
      domainId,
      domainName,
    }));
    
    // Continue inline - show KB details form instead of navigating
    setMessages(prev => [
      ...prev,
      { role: 'user', content: domainName },
      { role: 'assistant', content: 'kb-flow-details' }
    ]);
  };

  const handleKBCreationComplete = (kbId: string) => {
    // Reset KB flow state
    setIsKBCreationFlow(false);
    setKbFlowData({
      sourceType: null,
      sourceChannelId: null,
      channelName: '',
      domainId: null,
      domainName: '',
    });
    
    // Add success message to chat
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: `Great! Your Knowledge Base has been created. You can view it, add more content, or continue working on it.` }
    ]);
    
    toast.success('Knowledge Base created successfully!');
  };

  const handleKBCreationCancel = () => {
    setIsKBCreationFlow(false);
    setKbFlowData({
      sourceType: null,
      sourceChannelId: null,
      channelName: '',
      domainId: null,
      domainName: '',
    });
    
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: 'KB creation cancelled. Let me know if you need anything else!' }
    ]);
  };

  // Reset all workflow state for a fresh session when switching workflows
  const resetForNewWorkflow = () => {
    setMessages([]);
    setIsCreatingCall(false);
    setPreselectedCallType(null);
    callFlow.reset();
    setIsCreatingSprint(false);
    setIsKBCreationFlow(false);
    setKbFlowData({ sourceType: null, sourceChannelId: null, channelName: '', domainId: null, domainName: '' });
    setIsImportingContent(false);
    setConversationId(null);
    setIsDraftConversation(true);
    setWorkflowState({});
    setThreadPurpose('general');
    setSubmittedFormMessageIdx(null);
    setSelectedMessageIdx(null);
  };

  // Handler for Engage menu actions
  const handleEngageAction = (action: EngageAction) => {
    switch (action) {
      case 'call-solutions':
        resetForNewWorkflow();
        setThreadPurpose('call_creation');
        setPreselectedCallType('solution');
        callFlow.updateFormData('type', 'solution');
        setIsCreatingCall(true);
        setMessages([{
          role: 'assistant',
          content: 'call-flow-channel-source',
        }]);
        break;
      case 'call-knowledge':
        resetForNewWorkflow();
        setThreadPurpose('call_creation');
        setPreselectedCallType('knowledge');
        callFlow.updateFormData('type', 'knowledge');
        setIsCreatingCall(true);
        setMessages([{
          role: 'assistant',
          content: 'call-flow-channel-source',
        }]);
        break;
      case 'call-destruction':
        resetForNewWorkflow();
        setThreadPurpose('call_creation');
        setPreselectedCallType('destruction');
        callFlow.updateFormData('type', 'destruction');
        setIsCreatingCall(true);
        setMessages([{
          role: 'assistant',
          content: 'call-flow-channel-source',
        }]);
        break;
      case 'team-sprint':
        resetForNewWorkflow();
        setThreadPurpose('team_collaboration');
        setIsCreatingSprint(true);
        break;
      case 'invite-colleague':
        setInviteDialogOpen(true);
        break;
      case 'create-channel':
        setChannelDialogOpen(true);
        break;
      case 'create-kb':
        resetForNewWorkflow();
        setThreadPurpose('kb_work');
        setIsKBCreationFlow(true);
        setMessages([{
          role: 'assistant',
          content: 'kb-flow-channel-source',
        }]);
        break;
      case 'import-content':
        resetForNewWorkflow();
        setThreadPurpose('kb_work');
        setIsImportingContent(true);
        break;
      case 'compose':
        navigate('/prompt-architecture-sprint');
        break;
      case 'consume':
        navigate('/ispai-solutions');
        break;
      case 'give-feedback':
        setFeedbackDialogOpen(true);
        break;
      case 'direct-message':
        setDmDialogOpen(true);
        break;
      case 'connect-kbs':
        setShowKBPanel(prev => !prev);
        break;
      case 'view-history':
        setShowChronological(prev => !prev);
        break;
      case 'view-threads':
        setShowThreadHistory(true);
        break;
    }
  };

  return (
    <>
      <GibletChatHistory
        open={showThreadHistory}
        onOpenChange={setShowThreadHistory}
        userId={userId || ''}
        currentVariant={variantKey}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
      />

      <div className="h-full flex flex-col bg-background border rounded-lg overflow-hidden">
        {/* Header - Simplified Lovable-style */}
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                // Trigger title generation if conversation has messages but still has default title
                if (conversationId && messages.length >= 2) {
                  const firstUserMsg = messages.find(m => m.role === 'user');
                  const firstAssistantMsg = messages.find(m => m.role === 'assistant');
                  if (firstUserMsg && firstAssistantMsg) {
                    generateThreadTitle(firstUserMsg.content, firstAssistantMsg.content, conversationId);
                  }
                }
                onClose?.();
              }}
              className="h-8 w-8 rounded-full overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
              title="Close and save chat"
            >
              <img 
                src={gibletLogoGreen} 
                alt="Giblet" 
                className="h-8 w-8 rounded-full object-cover"
              />
            </button>
            <span className="font-medium text-foreground">{variant}</span>
          </div>
        </div>

        {/* Connected KB Chips */}
        {connectedKBs.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border-b">
            {connectedKBs.map(kbId => {
              const kb = allKBs?.find(k => k.id === kbId);
              return kb ? (
                <KBChip
                  key={kbId}
                  kbId={kbId}
                  title={kb.title}
                  onRemove={() => disconnectKB(kbId)}
                />
              ) : null;
            })}
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1 overflow-hidden px-4 py-3">
          <div 
            className="space-y-4"
            style={{ 
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'top center',
              minHeight: `${100 / (zoomLevel / 100)}%`
            }}
           >
            <div ref={scrollTopRef} />
            {messages.map((msg, idx) => {
            // Handle call flow messages
            if (msg.role === 'assistant' && msg.content.startsWith('call-flow-')) {
              if (msg.content === 'call-flow-channel-source') {
                return (
                  <div key={idx} className="flex justify-start">
                    <ChannelSourceSelectionMessage 
                      userChannels={userChannels || []}
                      userAvatarUrl={profile?.avatar_url}
                      userName={profile?.display_name}
                      onSelect={handleChannelSourceSelect}
                      disabled={isLoading}
                    />
                  </div>
                );
              }
              if (msg.content === 'call-flow-type') {
                return (
                  <div key={idx} className="flex justify-start">
                    <CallTypeMessage onSelect={handleCallTypeSelect} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-functional-area') {
                return (
                  <div key={idx} className="flex justify-start">
                    <FunctionalAreaMessage 
                      onSelect={handleFunctionalAreaSelect} 
                      callType={callFlow.formData.type!}
                      channelId={callFlow.formData.sourceChannelId}
                      isPersonal={callFlow.formData.sourceType === 'personal'}
                      disabled={isLoading}
                    />
                  </div>
                );
              }
              // CfK-specific: Connection to CfS question
              if (msg.content === 'call-flow-cfk-connection') {
                return (
                  <div key={idx} className="flex justify-start">
                    <CfKConnectionMessage onSelect={handleCfKConnectionSelect} disabled={isLoading} />
                  </div>
                );
              }
              // CfK-specific: CfS bounty question
              if (msg.content === 'call-flow-cfk-bounty') {
                return (
                  <div key={idx} className="flex justify-start">
                    <CfKBountyConnectionMessage onSelect={handleCfKBountySelect} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-bounty') {
                return (
                  <div key={idx} className="flex justify-start">
                    <BountyMessage onSelect={handleBountySelect} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-bounty-details') {
                return (
                  <div key={idx} className="flex justify-start">
                    <BountyDetailsMessage onSubmit={handleBountyDetailsSubmit} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-details') {
                return (
                  <div key={idx} className="flex justify-start">
                    <CallDetailsMessage onSubmit={handleCallDetailsSubmit} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-roles') {
                return (
                  <div key={idx} className="flex justify-start">
                    <RolesMessage onSubmit={handleRolesSubmit} disabled={isLoading} />
                  </div>
                );
              }
              if (msg.content === 'call-flow-review') {
                return (
                  <div key={idx} className="flex justify-start">
                    <ReviewMessage 
                      formData={callFlow.formData}
                      onConfirm={handleCallReviewConfirm}
                      onEdit={handleCallReviewEdit}
                      disabled={isLoading}
                    />
                  </div>
                );
              }
            }
            
            // Handle KB creation flow messages
            if (msg.role === 'assistant' && msg.content.startsWith('kb-flow-')) {
              if (msg.content === 'kb-flow-channel-source') {
                return (
                  <div key={idx} className="flex justify-start">
                    <ChannelSourceSelectionMessage 
                      userChannels={userChannels || []}
                      userAvatarUrl={profile?.avatar_url}
                      userName={profile?.display_name}
                      onSelect={handleKBChannelSourceSelect}
                      disabled={isLoading}
                      contextLabel="Knowledge Base"
                    />
                  </div>
                );
              }
              if (msg.content === 'kb-flow-domain') {
                return (
                  <div key={idx} className="flex justify-start">
                    <FunctionalAreaMessage 
                      onSelect={handleKBDomainSelect}
                      callType="knowledge"
                      channelId={kbFlowData.sourceChannelId}
                      isPersonal={kbFlowData.sourceType === 'personal'}
                      disabled={isLoading}
                      contextLabel="kb"
                    />
                  </div>
                );
              }
              if (msg.content === 'kb-flow-details') {
                return (
                  <div key={idx} className="flex justify-start">
                    <KBCreationInlineForm
                      userId={userId || ''}
                      channelId={kbFlowData.sourceChannelId}
                      channelName={kbFlowData.channelName}
                      domainId={kbFlowData.domainId}
                      domainName={kbFlowData.domainName}
                      onComplete={handleKBCreationComplete}
                      onCancel={handleKBCreationCancel}
                      disabled={isLoading}
                    />
                  </div>
                );
              }
            }
            
            // Regular message rendering
            const hasQuestionPrompt = msg.role === 'assistant' && msg.content.includes('QUESTION_PROMPT:');
            const isFormSubmitted = submittedFormMessageIdx === idx;
            
            const displayContent = hasQuestionPrompt 
              ? msg.content.replace(/QUESTION_PROMPT:\s*{[\s\S]*?}\s*(?:\n|$)/, '').trim()
              : msg.content;
            
            // Parse KB search results from response
            const kbResults = msg.role === 'assistant' ? parseKBResultsFromResponse(displayContent) : null;
            
            // Track recent KB results for context-aware navigation
            if (kbResults && kbResults.length > 0) {
              const newResults = kbResults.map(kb => ({ id: kb.id, title: kb.title }));
              if (JSON.stringify(newResults) !== JSON.stringify(recentKBResults)) {
                setRecentKBResults(newResults);
              }
            }
            
            // Remove KB_RESULTS JSON from display content
            const cleanedContent = kbResults 
              ? displayContent.replace(/KB_RESULTS:\s*\[[\s\S]*?\]/, '').trim()
              : displayContent;
            
            const selectableOptions = msg.role === 'assistant' ? detectSelectableOptions(cleanedContent) : null;
            const hasSelectableOptions = selectableOptions && selectableOptions.length > 0;
            
            const textContent = hasSelectableOptions 
              ? cleanedContent.split('\n').filter(line => !/^[-*]\s+/.test(line.trim())).join('\n').trim()
              : cleanedContent;
            
            // Handle KB selection from results
            const handleKBSelect = (kb: KBSearchResult) => {
              if (!connectedKBs.includes(kb.id)) {
                connectKB(kb.id);
                toast.success(`Connected: ${kb.title}`);
              }
            };
            
            return (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  {textContent && (
                    <div 
                      dangerouslySetInnerHTML={{ __html: stripMarkdownFormatting(textContent) }}
                      className="text-sm leading-relaxed whitespace-pre-wrap break-words"
                    />
                  )}
                  
                  {/* Render KB search results as interactive cards */}
                  {kbResults && kbResults.length > 0 && (
                    <KBSearchResults
                      results={kbResults}
                      onSelect={handleKBSelect}
                    />
                  )}
                  
                  {msg.citations && <CitationList citations={msg.citations} />}
                  {msg.role === 'assistant' && conversationId && (
                    <MessageFeedback conversationId={conversationId} messageIndex={idx} />
                  )}
                  
                  {hasSelectableOptions && (
                    <SelectionButtons
                      options={selectableOptions}
                      onSelect={(option) => handleOptionSelect(option, idx)}
                      disabled={isLoading || selectedMessageIdx === idx}
                    />
                  )}
                  
                  {hasQuestionPrompt && (
                    <GibletQuestionForm
                      content={msg.content}
                      onSubmit={(answers) => handleFormSubmit(answers, idx)}
                      isSubmitted={isFormSubmitted}
                    />
                  )}
                </div>
              </div>
            );
            })}
            
            {/* Inline Import Content Form */}
            {isImportingContent && userId && (
              <ImportContentInlineForm
                userId={userId}
                onComplete={(kbId) => {
                  setIsImportingContent(false);
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `✅ Your Knowledge Base has been published successfully! You can view it in your profile or continue working here.`
                  }]);
                  toast.success('Knowledge Base published!');
                }}
                onCancel={() => {
                  setIsImportingContent(false);
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: 'No problem! Let me know if you want to try importing content again later.'
                  }]);
                }}
              />
            )}
            
            {/* Inline Sprint Creation Form */}
            {isCreatingSprint && userId && (
              <SprintCreationInlineForm
                userId={userId}
                onComplete={(sprintId) => {
                  setIsCreatingSprint(false);
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `✅ Your Team Sprint has been launched! Redirecting to the sprint management page...`
                  }]);
                  navigate(`/prompt-architecture-sprint?sprint=${sprintId}`);
                }}
                onCancel={() => {
                  setIsCreatingSprint(false);
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: 'No problem! Let me know when you want to start a Team Sprint.'
                  }]);
                }}
              />
            )}
            
            {messages.length === 0 && !isImportingContent && !isCreatingSprint && (
              <div className="flex justify-center items-center h-32">
                <p className="text-muted-foreground text-sm">Start a conversation with {variant}</p>
              </div>
            )}
            {isLoading && (
              <GibletThinkingIndicator statusText={loadingStatus} />
            )}
            <div ref={scrollRef} />
          </div>
        </ScrollArea>

        {/* KB Connection Panel (shown when triggered from + menu) */}
        {showKBPanel && (
          <div className="border-t p-3">
            <KBConnectionPanel
              connectedKBs={connectedKBs}
              onConnect={connectKB}
              onDisconnect={disconnectKB}
              canConnectMore={canConnectMore}
              recentlyUsed={getRecentlyUsed()}
              maxConnectedKBs={maxConnectedKBs}
            />
          </div>
        )}

        {/* Chronological History (shown when triggered from + menu) */}
        {showChronological && userId && (
          <div className="border-t">
            <GibletChronologicalHistory
              userId={userId}
              currentVariant={variantKey}
              expanded={showChronological}
              onToggle={() => setShowChronological(!showChronological)}
            />
          </div>
        )}

        {/* Lovable-style Input Area */}
        <GibletInputArea
          value={input}
          onChange={setInput}
          onSend={handleSend}
          onEngageAction={handleEngageAction}
          isLoading={isLoading}
          connectedKBsCount={connectedKBs.length}
          maxKBs={maxConnectedKBs}
        />
      </div>

      {/* Invite Colleague Dialog */}
      <InviteColleagueDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
      />

      {/* Create Channel Dialog */}
      <CreateChannelDialog
        open={channelDialogOpen}
        onOpenChange={setChannelDialogOpen}
      />

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        pageRoute={location.pathname}
        pageName={getPageName(location.pathname)}
      />

      {/* Direct Message Dialog */}
      <DirectMessageDialog
        open={dmDialogOpen}
        onOpenChange={setDmDialogOpen}
      />
    </>
  );
};
